import React, { useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useDebounce } from '@node-space/hooks'
import {
  Box,
  Callout,
  EmptyState,
  Box as FormRow,
  Input,
  RowsSkeletonLoader,
  toast,
} from '@node-space/ui-components'
import { Lottie } from 'components/Animations/SimpleLottie'
import { SEARCH_INPUT_DEBOUNCE_TIME } from 'constants/General'
import { MIN_PAGINATION_SIZE } from 'constants/queries'
import { useAccountsContext } from 'hooks/context/useAccountsContext'
import { useAddNetworkConnectionMutation } from 'hooks/mutations/BvnkNetwork/useAddNetworkConnectionMutation'
import { useNetworkMembersQuery } from 'hooks/queries/BvnkNetwork/useNetworkMembersQuery'
import { NetworkMember } from 'types/bvnkNetwork'
import { useBvnkMemberSearch } from '../hooks/useBvnkMemberSearch'
import NetworkMemberConnectionsList from './NetworkMemberConnectionsList'
import { NetworkMembersEmptyState } from './NetworkMembersEmptyState'

interface SentRequestsHistory {
  [property: string]: boolean
}

export const DiscoverNetworkMembers = () => {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const { currentAccount } = useAccountsContext()
  const { mutate: addNetworkConnection, isPending: isUpdatingConnection } =
    useAddNetworkConnectionMutation()
  const {
    data: networkMembers,
    isFetching: isFetchingNetworkMembers,
    isError: isFetchingNetworkMembersError,
  } = useNetworkMembersQuery(currentAccount?.reference, true)

  const [sentRequests, setSentRequests] = useState<SentRequestsHistory>({})

  // Phase 1: FE filter
  // Phase 2: Add BE search and pagination
  const [query, setQuery] = useState('')
  const debouncedSearchTerm: string = useDebounce(query, SEARCH_INPUT_DEBOUNCE_TIME)

  const { filteredNetworkMembers, isFiltering } = useBvnkMemberSearch(
    debouncedSearchTerm,
    networkMembers
  )

  const isLoading = useMemo(() => {
    return isFetchingNetworkMembers || isFiltering
  }, [isFetchingNetworkMembers, isFiltering])

  const onNetworkMemberConnectHandler = (networkMember: NetworkMember) => {
    const { accountReference, name } = networkMember?.account || {}

    addNetworkConnection(
      {
        requesterAccount: currentAccount?.reference,
        receiverAccount: accountReference,
      },
      {
        onSuccess: () => {
          setSentRequests({ ...sentRequests, [accountReference]: true })
          queryClient.invalidateQueries({ queryKey: ['bvnkNetworkConnections'] })
        },
        onError: () => {
          toast({
            header: t('bvnkNetwork.addNetworkMemberError', { networkMember: name }),
            variant: 'error',
          })
        },
      }
    )
  }

  const onSearchTermChange = (searchTermValue: string) => {
    setQuery(searchTermValue)
  }

  return (
    <Box testId="discover-members-container" padding={20} className="max-h-[75vh] w-full">
      {isLoading ? (
        <RowsSkeletonLoader rows={MIN_PAGINATION_SIZE} />
      ) : (
        <>
          {isFetchingNetworkMembersError ? (
            <Callout variant="error">{t('bvnkNetwork.discoverMembersError')}</Callout>
          ) : (
            <>
              {!filteredNetworkMembers?.length ? (
                <NetworkMembersEmptyState />
              ) : (
                <>
                  <FormRow width="full">
                    <Input
                      testId="network-member-search-field"
                      onChange={event => onSearchTermChange(event.target.value)}
                      searchable={true}
                      value={query}
                      placeholderText={t('bvnkNetwork.networkMembersSearchPlaceholder')}
                    />
                  </FormRow>

                  {!!query && !filteredNetworkMembers?.length ? (
                    <EmptyState
                      testId="empty-state"
                      LottiePlayer={<Lottie src="no-data" />}
                      title={t('bvnkNetwork.noSearchResultHeadline')}
                      description={t('bvnkNetwork.noSearchResultDescription')}
                    />
                  ) : (
                    <Box className="mt-4 max-h-[75vh] overflow-visible">
                      <NetworkMemberConnectionsList
                        networkMembers={filteredNetworkMembers}
                        sentRequests={sentRequests}
                        connectToNetworkMember={onNetworkMemberConnectHandler}
                        isUpdatingConnection={isUpdatingConnection}
                      />
                    </Box>
                  )}
                </>
              )}
            </>
          )}
        </>
      )}
    </Box>
  )
}
